/**
 * FeaturesSection — the "EVERYTHING A STUDY NEEDS" capability grid, a single
 * contiguous BRUTALIST TABLE (no gaps) in RetroUI + Tailwind: a teal "WHAT YOU GET"
 * eyebrow over a big black `font-head` heading, then a seamless 6-cell grid (3 cols
 * lg / 2 md / 1 sm). Cells share 2px black borders with NO gaps via the
 * no-double-border technique: the GRID CONTAINER owns the outer top+left edge
 * (`border-l-2 border-t-2`), and each card owns its right+bottom edge
 * (`border-r-2 border-b-2`). Each card is a white surface with a filled icon box, a
 * bold title, and a muted description.
 *
 * FILLED ICONS: each icon box is FILLED with that card's accent brand color
 * (bg-primary / bg-secondary / bg-yellow / bg-green); the glyph is a contrasting
 * solid (white on the dark fills; near-black `text-foreground` on yellow). Box keeps
 * its 2px black border.
 *
 * THE HOVER: on hover the card LIFTS (translate) toward its OUTWARD grid position and
 * reveals a 3-LAYER STACKED hard 0-blur offset shadow — Teal → Cardinal → Yellow at
 * 5/10/15px — on the OPPOSITE side. The lift distance equals the outermost (15px)
 * shadow offset, so the shadow's far edge pins to the card's ORIGINAL footprint (it
 * peels up off a static backplate). The lifted card's top+left edges (otherwise
 * borderless) are redrawn as 2px black INSET shadow so it stays fully outlined.
 * Direction is mapped per index against the lg 3×2 grid (row-major):
 *   idx 0 TOP-LEFT  → lift up-left,    shadow bottom-right
 *   idx 1 TOP-MID   → lift straight up, shadow straight down
 *   idx 2 TOP-RIGHT → lift up-right,   shadow bottom-left
 *   idx 3 BOT-LEFT  → lift down-left,  shadow top-right
 *   idx 4 BOT-MID   → lift straight down, shadow straight up
 *   idx 5 BOT-RIGHT → lift down-right, shadow top-left
 * (Directions target the lg layout; md/sm won't perfectly mirror, which is fine.)
 *
 * NO HOVER FLICKER: each card is split into a STATIC transparent OUTER (the `group`,
 * which receives :hover and `z-10`) wrapping the visible INNER card (which does the
 * lift + shadow on `group-hover`). Because the hit-area (outer) never moves, hovering
 * the revealed shadow/border zone can't toggle hover off — the card can't "walk out"
 * from under the cursor, so it never flickers up and down. Transform + shadow + z
 * don't affect layout, so the hover also never reflows the page.
 *
 * Reduced motion: the lift/shadow are gated behind `motion-safe:` (and a JS
 * useReducedMotion fallback) → a crisp STATIC card. Tailwind Preflight is OFF →
 * borders are declared `border-solid`. ZERO MUI.
 */
import * as React from 'react';
import { useReducedMotion } from 'framer-motion';
import {
  Shield,
  Watch,
  Users,
  Download,
  RefreshCw,
  Microscope,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface Feature {
  title: string;
  desc: string;
  Icon: LucideIcon;
  /** Tailwind bg-color class FILLING the icon box (the card's accent brand color). */
  iconBg: string;
  /** Tailwind text-color class for the icon glyph, chosen for contrast over iconBg. */
  iconColor: string;
  /**
   * Full `group-hover` class string (directional lift + the 3-color stacked offset
   * shadow + 2px inset top/left edges), pre-baked per card so the exact
   * offsets/directions are statically present in the Tailwind content scan. Applied
   * to the INNER card; the static OUTER owns `z-10`. Only when motion is OK.
   */
  hover: string;
}

/* Accent COLOR cycle for the FILLED icon box is [Cardinal, Teal, Yellow, Teal,
 * Green, Cardinal]. The shadow is the SAME three brand colors (Teal→Cardinal→Yellow)
 * on every card; only the per-index DIRECTION (lift sign + shadow side) changes,
 * mapped to the lg 3×2 grid. Arbitrary values are written out in full so Tailwind
 * emits them. */
const FEATURES: Feature[] = [
  {
    // idx 0 TOP-LEFT — Cardinal fill, lift up-left, shadow bottom-right
    title: 'Secure & compliant',
    desc: 'HIPAA-aware data handling with enterprise-grade encryption for sensitive health information.',
    Icon: Shield,
    iconBg: 'bg-primary',
    iconColor: 'text-white',
    hover:
      'motion-safe:lg:group-hover:-translate-x-[15px] motion-safe:lg:group-hover:-translate-y-[15px] motion-safe:lg:group-hover:shadow-[5px_5px_0_0_#00505C,10px_10px_0_0_#8C1515,15px_15px_0_0_#F5C518,inset_0_2px_0_0_#000,inset_2px_0_0_0_#000]',
  },
  {
    // idx 1 TOP-MID — Teal fill, lift straight up, shadow straight down
    title: 'Wearable integration',
    desc: 'One-tap OAuth to Fitbit and Apple Health for continuous, passive data collection.',
    Icon: Watch,
    iconBg: 'bg-secondary',
    iconColor: 'text-white',
    hover:
      'motion-safe:lg:group-hover:-translate-y-[15px] motion-safe:lg:group-hover:shadow-[0px_5px_0_0_#00505C,0px_10px_0_0_#8C1515,0px_15px_0_0_#F5C518,inset_0_2px_0_0_#000,inset_2px_0_0_0_#000]',
  },
  {
    // idx 2 TOP-RIGHT — Yellow fill, lift up-right, shadow bottom-left
    title: 'Multi-researcher',
    desc: 'Collaborative studies with role-based access for whole research teams.',
    Icon: Users,
    iconBg: 'bg-yellow',
    iconColor: 'text-foreground',
    hover:
      'motion-safe:lg:group-hover:translate-x-[15px] motion-safe:lg:group-hover:-translate-y-[15px] motion-safe:lg:group-hover:shadow-[-5px_5px_0_0_#00505C,-10px_10px_0_0_#8C1515,-15px_15px_0_0_#F5C518,inset_0_2px_0_0_#000,inset_2px_0_0_0_#000]',
  },
  {
    // idx 3 BOT-LEFT — Teal fill, lift down-left, shadow top-right
    title: 'Data export',
    desc: 'Export raw or summarized datasets — CSV, JSON, or analysis-ready bundles.',
    Icon: Download,
    iconBg: 'bg-secondary',
    iconColor: 'text-white',
    hover:
      'motion-safe:lg:group-hover:-translate-x-[15px] motion-safe:lg:group-hover:translate-y-[15px] motion-safe:lg:group-hover:shadow-[5px_-5px_0_0_#00505C,10px_-10px_0_0_#8C1515,15px_-15px_0_0_#F5C518,inset_0_2px_0_0_#000,inset_2px_0_0_0_#000]',
  },
  {
    // idx 4 BOT-MID — Green fill, lift straight down, shadow straight up
    title: 'Real-time sync',
    desc: 'Automatic background ingestion with live participant status tracking.',
    Icon: RefreshCw,
    iconBg: 'bg-green',
    iconColor: 'text-white',
    hover:
      'motion-safe:lg:group-hover:translate-y-[15px] motion-safe:lg:group-hover:shadow-[0px_-5px_0_0_#00505C,0px_-10px_0_0_#8C1515,0px_-15px_0_0_#F5C518,inset_0_2px_0_0_#000,inset_2px_0_0_0_#000]',
  },
  {
    // idx 5 BOT-RIGHT — Cardinal fill, lift down-right, shadow top-left
    title: 'Research-focused',
    desc: 'Built for academic and clinical research — IRB protocols baked in.',
    Icon: Microscope,
    iconBg: 'bg-primary',
    iconColor: 'text-white',
    hover:
      'motion-safe:lg:group-hover:translate-x-[15px] motion-safe:lg:group-hover:translate-y-[15px] motion-safe:lg:group-hover:shadow-[-5px_-5px_0_0_#00505C,-10px_-10px_0_0_#8C1515,-15px_-15px_0_0_#F5C518,inset_0_2px_0_0_#000,inset_2px_0_0_0_#000]',
  },
];

const FeaturesSection: React.FC = () => {
  const reduceMotion = useReducedMotion();

  return (
    <section
      id="features"
      className="bg-background px-4 py-16 md:px-8 md:py-24"
    >
      {/* Header — small outlined eyebrow chip + big black uppercase heading (5.png). */}
      <div className="mx-auto max-w-6xl text-center">
        <span className="mb-4 inline-block border-2 border-solid border-black bg-primary px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white">
          What you get
        </span>
        <h2 className="font-head text-3xl uppercase leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          <span className="text-primary">Everything</span> a study needs
        </h2>
      </div>

      {/*
        6-cell brutalist TABLE: 1 / 2 / 3 cols, NO gap. The container supplies the
        outer top+left border; each card supplies its right+bottom border, so the
        grid is complete with no doubled and no missing edges at every breakpoint.
      */}
      <div className="mx-auto mt-12 grid max-w-6xl grid-cols-1 border-l-2 border-t-2 border-solid border-black sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map(({ title, desc, Icon, iconBg, iconColor, hover }) => (
          // OUTER = STATIC transparent hit-area (the `group`). It never moves, so
          // hovering the lifted card's revealed shadow/border zone can't toggle hover
          // off → no flicker. It carries `z-10` on hover so the lifted INNER + its
          // shadow render above neighbouring cells.
          <div key={title} className="group relative motion-safe:lg:hover:z-10">
            {/* INNER = the visible brutalist card. Owns its right+bottom grid edges +
                opaque bg; LIFTS + casts its shadow on group-hover. `h-full` so short
                cards still fill the row-stretched cell. */}
            <div
              className={[
                'h-full border-b-2 border-r-2 border-solid border-black bg-card p-6 transition-all duration-150 ease-out',
                reduceMotion ? '' : hover,
              ].join(' ')}
            >
              {/* Icon in its own small framed box (border-2 black) FILLED with the accent color. */}
              <div
                className={`mb-4 inline-flex h-11 w-11 items-center justify-center border-2 border-solid border-black ${iconBg}`}
              >
                <Icon className={`h-5 w-5 ${iconColor}`} strokeWidth={2.5} aria-hidden />
              </div>
              <h3 className="font-head text-lg uppercase leading-snug tracking-tight text-foreground">
                {title}
              </h3>
              <p className="mt-2 font-sans text-sm leading-relaxed text-muted-foreground">
                {desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeaturesSection;
